import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Hotel, Volume2, Settings, Layers, ArrowRight, Check } from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5 }
};

// Caractéristiques (sourced HomePage L267 + brief FLOW_OK : exploitation, maintenance, modulable, installation discrète)
const features = [
  { icon: Hotel,    title: "Installation discrète",           desc: "Systèmes performants intégrés de façon discrète dans les espaces hospitality." },
  { icon: Layers,   title: "Modulable & multi-zones",         desc: "Régulation chambre par chambre pour un pilotage individualisé de chaque espace." },
  { icon: Volume2,  title: "Fonctionnement silencieux",       desc: "Confort client garanti jour et nuit, adapté aux environnements sensibles." },
  { icon: Settings, title: "Exploitation et maintenance",     desc: "Contrats de maintenance préventive et interventions d'urgence toute l'année." }
];

const brands = ["Daikin", "Mitsubishi", "Hitachi", "Bosch"];
const badges = ["Techniciens certifiés", "Garantie décennale", "SAV 7j/7", "Devis gratuit"];

export default function HotelleriePage() {
  return (
    <>
      <Helmet>
        <title>Climatisation hôtels Paris | FLOW</title>
        <meta name="description" content="FLOW installe des systèmes de climatisation discrets, modulables et performants pour hôtels boutique et hospitality, avec régulation chambre par chambre. Marques Daikin, Mitsubishi, Hitachi, Bosch." />
        <link rel="canonical" href="https://flow-air.com/secteurs/hotellerie" />
      </Helmet>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#F5F7FA] to-white py-16 md:py-24" data-testid="hotellerie-hero">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <span className="caption mb-4 block">Secteur — Hôtellerie</span>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0E1A2B] mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Climatisation pour <span className="text-[#00AFFF]">hôtels boutique</span> et hospitality
            </h1>
            <p className="text-lg text-[#475569] leading-relaxed">
              FLOW accompagne les professionnels de l'hôtellerie dans l'installation de systèmes
              de climatisation et ventilation discrets, modulables et performants, avec régulation
              chambre par chambre.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Caractéristiques */}
      <section className="py-16 md:py-24 bg-white" data-testid="hotellerie-features">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center mb-12 max-w-2xl mx-auto">
            <span className="caption mb-4 block">Savoir-faire</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0E1A2B]" style={{ fontFamily: 'Sora, sans-serif' }}>
              Caractéristiques hôtellerie
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  className="p-6 bg-[#F5F7FA] rounded-2xl border border-transparent hover:border-[#00AFFF]/30 transition-colors"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`hotellerie-feature-${index}`}
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center mb-4">
                    <Icon size={22} className="text-[#00AFFF]" />
                  </div>
                  <h3 className="text-base font-semibold mb-2 text-[#0E1A2B]">{feature.title}</h3>
                  <p className="text-[#475569] text-sm leading-relaxed">{feature.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Marques installées */}
      <section className="py-12 md:py-16 bg-[#F5F7FA]">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center">
            <span className="caption mb-6 block">Marques installées</span>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
              {brands.map((brand) => (
                <span
                  key={brand}
                  className="text-lg font-bold tracking-wider text-[#94A3B8] hover:text-[#0E1A2B] transition-colors"
                >
                  {brand.toUpperCase()}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Badges globaux */}
      <section className="py-12 bg-white">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-2xl mx-auto">
            {badges.map((item, i) => (
              <div key={i} className="flex items-center justify-center gap-2 py-3 px-4 bg-[#F5F7FA] rounded-lg">
                <div className="w-6 h-6 rounded-md bg-gradient-to-br from-[#00AFFF]/15 to-[#00AFFF]/5 flex items-center justify-center">
                  <Check size={12} className="text-[#00AFFF]" />
                </div>
                <span className="text-xs font-medium text-[#0E1A2B]">{item}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-[#0E1A2B]">
        <div className="container-flow">
          <motion.div {...fadeInUp} className="text-center max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Sora, sans-serif' }}>
              Parlons de votre projet
            </h2>
            <p className="text-[#94A3B8] mb-8 leading-relaxed">
              Contactez l'équipe FLOW pour échanger sur votre besoin en climatisation et ventilation.
            </p>
            <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="hotellerie-cta-contact">
              Demander un devis <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
}
